// Hasil CSAT — hanya pemegang izin modul "csat" (Manager & Owner).
// Backend /csat/hasil tidak pernah mengirim identitas pelanggan, halaman ini
// hanya menampilkan agregat + komentar anonim per frontliner.
//
// Fase 5.2: pertanyaan survei sekarang bisa lebih dari 1 (dikonfigurasi owner
// lewat CsatPengaturan.jsx) — "pertanyaan_ringkasan" dari GET /csat/hasil
// menampilkan rincian PER PERTANYAAN (rata-rata bintang lain di luar rating
// utama, distribusi opsi pilihan ganda). Kartu KPI & tabel per-frontliner di
// atas TETAP memakai rating utama saja (field top-level lama), sengaja tidak
// diubah supaya definisi "rata-rata keseluruhan" tetap konsisten dengan histori.
//
// 31 Agu 2026: panel "Perangkat Pengisi Survei" (brief owner 30 Agu §3).
// Sengaja AGREGAT saja — tidak ada satu pun tempat di halaman ini yang
// menempelkan tipe HP pada baris komentar atau baris frontliner. Pelanggan
// dijanjikan tertulis di halaman survei bahwa jawabannya tidak bisa dilacak ke
// dirinya; "iPhone" di sebelah satu komentar pedas sudah cukup untuk menebak
// siapa penulisnya di cabang yang sepi. Backend pun tidak mengirimkannya
// per-baris (routers/csat.py::_rekap_perangkat) — jangan menambahkannya di
// sini tanpa keputusan owner yang tertulis.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Star, MessageSquareText, Users, Lock, TrendingUp, Send, Settings2, ListChecks, Smartphone } from "lucide-react";
import { bulanIniISO } from "@/lib/tanggal";

function Stars({ value, size = "w-4 h-4" }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} ${n <= Math.round(value) ? "text-amber-400 fill-amber-400" : "text-slate-200"}`}
        />
      ))}
    </span>
  );
}

function BarOpsi({ distribusi }) {
  const entries = Object.entries(distribusi || {});
  const total = entries.reduce((s, [, c]) => s + c, 0);
  if (entries.length === 0) {
    return <p className="text-xs text-slate-400 italic">Belum ada jawaban.</p>;
  }
  return (
    <div className="flex flex-col gap-1">
      {entries.map(([opsi, c]) => {
        const pct = total ? (c / total) * 100 : 0;
        return (
          <div key={opsi} className="flex items-center gap-2">
            <span className="text-xs text-slate-600 w-28 truncate shrink-0" title={opsi}>{opsi}</span>
            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-sky-500" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs text-slate-500 w-6 text-right">{c}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Daftar batang untuk rekap agregat [{nama, jumlah, persen}] — dipakai panel
    perangkat. Beda dari BarOpsi: sumbernya sudah membawa persen dari server,
    jadi angka di layar identik dengan angka yang dipakai backend (tidak ada
    dua pembulatan yang bisa berpisah jalan). */
function BarRekap({ baris, warna = "bg-emerald-500", kosong = "Belum ada data." }) {
  if (!baris || baris.length === 0) {
    return <p className="text-xs text-slate-400 italic">{kosong}</p>;
  }
  return (
    <div className="flex flex-col gap-1.5">
      {baris.map((b) => (
        <div key={b.nama} className="flex items-center gap-2">
          <span className="text-xs text-slate-600 w-28 truncate shrink-0" title={b.nama}>{b.nama}</span>
          <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full ${warna}`} style={{ width: `${b.persen}%` }} />
          </div>
          <span className="text-xs font-medium text-slate-700 w-12 text-right">{b.persen}%</span>
          <span className="text-[11px] text-slate-400 w-6 text-right">{b.jumlah}</span>
        </div>
      ))}
    </div>
  );
}

/** Satu kartu ringkasan untuk 1 pertanyaan (bintang / pilihan / teks). */
function KartuPertanyaan({ p }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4" data-testid={`csat-ringkasan-${p.pertanyaan_id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-semibold text-slate-800 leading-snug">
          {p.label}
          {p.rating_utama && (
            <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              Rating Utama
            </span>
          )}
        </p>
      </div>
      <p className="text-xs text-slate-400 mb-3">{p.jumlah_terjawab} jawaban</p>

      {p.tipe === "bintang" && (
        <div className="flex items-center gap-2 mb-2">
          <span className="font-display text-xl font-bold text-slate-900">{p.rata_rata ?? "-"}</span>
          {p.rata_rata != null && <Stars value={p.rata_rata} size="w-3.5 h-3.5" />}
        </div>
      )}
      {p.tipe === "bintang" && p.distribusi && (
        <div className="flex flex-col gap-0.5">
          {[5, 4, 3, 2, 1].map((n) => {
            const c = p.distribusi[String(n)] || 0;
            const pct = p.jumlah_terjawab ? (c / p.jumlah_terjawab) * 100 : 0;
            return (
              <div key={n} className="flex items-center gap-1.5">
                <span className="text-[10px] text-slate-400 w-2 text-right">{n}</span>
                <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full ${n >= 4 ? "bg-emerald-500" : n === 3 ? "bg-amber-400" : "bg-rose-400"}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="text-[10px] text-slate-400 w-4">{c}</span>
              </div>
            );
          })}
        </div>
      )}
      {p.tipe === "pilihan" && <BarOpsi distribusi={p.distribusi_opsi} />}
      {p.tipe === "teks" && (
        <p className="text-xs text-slate-500 italic">Lihat komentar terkait di panel "Komentar Terbaru".</p>
      )}
    </div>
  );
}

function DistBar({ distribusi, jumlah }) {
  // Bar mini 5→1: manager langsung melihat apakah nilai rata-rata dibentuk
  // oleh mayoritas bintang 5 atau tertolong segelintir nilai ekstrem.
  return (
    <div className="flex flex-col gap-0.5 min-w-[120px]">
      {[5, 4, 3, 2, 1].map((n) => {
        const c = distribusi?.[String(n)] || 0;
        const pct = jumlah ? (c / jumlah) * 100 : 0;
        return (
          <div key={n} className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-400 w-2 text-right">{n}</span>
            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`h-full ${n >= 4 ? "bg-emerald-500" : n === 3 ? "bg-amber-400" : "bg-rose-400"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[10px] text-slate-400 w-4">{c}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function CsatResults() {
  // `toISOString()` selalu UTC — sebelum jam 07:00 WIB tanggal 1, `.slice(0,7)`
  // memberi BULAN SEBELUMNYA (lihat jebakan zona waktu di CLAUDE.md).
  // `bulanIniISO()` membaca jam lewat Intl dengan timeZone eksplisit.
  const [periode, setPeriode] = useState(bulanIniISO());
  const [semua, setSemua] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    const p = new URLSearchParams();
    if (!semua && periode) p.set("periode", periode);
    api.get(`/csat/hasil?${p}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((err) => {
        if (!alive) return;
        if (err.response?.status === 403) {
          setError("Anda tidak punya izin melihat hasil CSAT. Halaman ini khusus Manager & Owner.");
        } else {
          setError(err.response?.data?.detail || "Gagal memuat hasil CSAT.");
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [periode, semua]);

  const fmtTgl = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
    } catch {
      return iso.slice(0, 10);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px]" data-testid="csat-results">
      <header className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Customer Satisfaction</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">CSAT Pelanggan</h1>
          <p className="mt-2 text-sm text-slate-500">
            Penilaian pelanggan atas pelayanan frontliner, dikumpulkan lewat tautan WhatsApp setelah transaksi.
          </p>
        </div>
        <Link to="/csat-pengaturan" data-testid="csat-link-pengaturan"
          className="h-10 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium inline-flex items-center gap-2">
          <Settings2 className="w-4 h-4" /> Pengaturan Survei
        </Link>
      </header>

      {/* Banner kerahasiaan — mengingatkan pembaca bahwa data ini tidak untuk diteruskan ke sales */}
      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6" data-testid="csat-secret-banner">
        <Lock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">
          <span className="font-semibold">Rahasia Manager & Owner.</span> Pelanggan dijanjikan jawabannya tidak akan
          diketahui sales — jangan meneruskan komentar di halaman ini ke frontliner yang dinilai.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-6 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Periode</span>
          <input
            type="month"
            value={periode}
            disabled={semua}
            onChange={(e) => setPeriode(e.target.value)}
            data-testid="csat-filter-periode"
            className="mt-1 block h-10 px-3 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50 disabled:text-slate-400"
          />
        </label>
        <label className="flex items-center gap-2 h-10 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={semua}
            onChange={(e) => setSemua(e.target.checked)}
            data-testid="csat-filter-semua"
            className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-sm text-slate-600">Semua periode</span>
        </label>
      </div>

      {loading && <p className="text-sm text-slate-500">Memuat...</p>}
      {!loading && error && (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center" data-testid="csat-error">
          <Lock className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      )}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="csat-kpi-rata">
              <div className="w-10 h-10 rounded-lg grid place-items-center bg-amber-50 text-amber-500 mb-4">
                <Star className="w-5 h-5" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Rata-rata Keseluruhan</p>
              <div className="flex items-center gap-2">
                <p className="font-display text-2xl font-bold text-slate-900">{data.rata_rata_keseluruhan}</p>
                <Stars value={data.rata_rata_keseluruhan} />
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="csat-kpi-respons">
              <div className="w-10 h-10 rounded-lg grid place-items-center bg-emerald-50 text-emerald-600 mb-4">
                <MessageSquareText className="w-5 h-5" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Total Respons</p>
              <p className="font-display text-2xl font-bold text-slate-900">{data.total_respons}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="csat-kpi-frontliner">
              <div className="w-10 h-10 rounded-lg grid place-items-center bg-sky-50 text-sky-600 mb-4">
                <Users className="w-5 h-5" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Frontliner Dinilai</p>
              <p className="font-display text-2xl font-bold text-slate-900">{data.jumlah_frontliner}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="csat-kpi-tingkat">
              <div className="w-10 h-10 rounded-lg grid place-items-center bg-slate-100 text-slate-600 mb-4">
                <Send className="w-5 h-5" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Tingkat Respons</p>
              <p className="font-display text-2xl font-bold text-slate-900">{data.tingkat_respons}%</p>
              <p className="text-xs text-slate-400 mt-1">{data.total_undangan} tautan dikirim</p>
            </div>
          </div>

          {/* Ringkasan per pertanyaan (Fase 5.2) — muncul kalau survei punya
              lebih dari sekadar rating utama, mis. pertanyaan bintang kedua
              atau pilihan ganda. Kartu kosong (0 pertanyaan tambahan, cuma
              rating_utama) sengaja disembunyikan supaya tidak ada kartu
              duplikat dengan "Rata-rata Keseluruhan" di atas untuk instalasi
              yang belum pernah di-custom. */}
          {data.pertanyaan_ringkasan && data.pertanyaan_ringkasan.filter((p) => !p.rating_utama).length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <ListChecks className="w-4 h-4 text-emerald-600" />
                <h3 className="font-display font-semibold text-slate-900">Ringkasan per Pertanyaan</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="csat-ringkasan-pertanyaan">
                {data.pertanyaan_ringkasan.map((p) => <KartuPertanyaan key={p.pertanyaan_id} p={p} />)}
              </div>
            </div>
          )}

          {/* Rekap perangkat pengisi survei (brief owner 30 Agu 2026 §3).
              Dijaga optional-chaining: backend versi lama tidak mengirim
              "perangkat_ringkasan" sama sekali, dan halaman ini harus tetap
              tampil normal (bukan layar kosong) kalau itu terjadi. */}
          {data.perangkat_ringkasan && (
            <div className="mb-6 bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="csat-perangkat">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-emerald-600" />
                  <h3 className="font-display font-semibold text-slate-900">Perangkat Pengisi Survei</h3>
                </div>
                <p className="text-xs text-slate-500">
                  {data.perangkat_ringkasan.total_tercatat} respons tercatat
                  {data.perangkat_ringkasan.tidak_tercatat > 0 && (
                    <span data-testid="csat-perangkat-tidak-tercatat">
                      {" "}· {data.perangkat_ringkasan.tidak_tercatat} tidak tercatat
                    </span>
                  )}
                </p>
              </div>

              {data.perangkat_ringkasan.total_tercatat === 0 ? (
                <p className="px-6 py-10 text-center text-sm text-slate-500" data-testid="csat-perangkat-kosong">
                  Belum ada respons di periode ini yang perangkatnya tercatat.
                  {data.perangkat_ringkasan.tidak_tercatat > 0
                    ? ` ${data.perangkat_ringkasan.tidak_tercatat} respons masuk sebelum fitur ini dipasang, jadi perangkatnya memang tidak pernah direkam.`
                    : ""}
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-6 py-5">
                  <div data-testid="csat-perangkat-platform">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Sistem Operasi</p>
                    <BarRekap baris={data.perangkat_ringkasan.platform} />
                  </div>
                  <div data-testid="csat-perangkat-model">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                      Model Terbanyak
                      {data.perangkat_ringkasan.model_terbanyak && (
                        <span className="ml-2 normal-case tracking-normal text-emerald-700 font-medium">
                          {data.perangkat_ringkasan.model_terbanyak}
                        </span>
                      )}
                    </p>
                    <BarRekap baris={data.perangkat_ringkasan.model} warna="bg-sky-500" />
                  </div>
                  <div data-testid="csat-perangkat-browser">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Dibuka Lewat</p>
                    <BarRekap baris={data.perangkat_ringkasan.browser} warna="bg-violet-500" />
                  </div>
                </div>
              )}

              {data.perangkat_ringkasan.catatan && (
                <p className="px-6 pb-4 text-xs text-slate-400 leading-relaxed" data-testid="csat-perangkat-catatan">
                  {data.perangkat_ringkasan.catatan}
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tabel per frontliner */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-display font-semibold text-slate-900">Skor per Frontliner</h3>
                <p className="text-xs text-slate-500">Diurutkan dari rata-rata tertinggi</p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    <th className="text-left py-3 px-4">Frontliner</th>
                    <th className="text-left py-3 px-4">Branch</th>
                    <th className="text-center py-3 px-4">Respons</th>
                    <th className="text-left py-3 px-4">Rata-rata</th>
                    <th className="text-left py-3 px-4">Distribusi</th>
                  </tr>
                </thead>
                <tbody>
                  {data.frontliners.map((f, i) => (
                    <tr key={f.nama_penjual} data-testid={`csat-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 font-medium text-slate-900">{f.nama_penjual}</td>
                      <td className="py-3 px-4 text-xs text-slate-500">{f.branch || "-"}</td>
                      <td className="py-3 px-4 text-center font-semibold">{f.jumlah_respons}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className={`font-semibold ${f.rata_rata >= 4 ? "text-emerald-700" : f.rata_rata >= 3 ? "text-amber-600" : "text-rose-600"}`}>
                            {f.rata_rata}
                          </span>
                          <Stars value={f.rata_rata} size="w-3.5 h-3.5" />
                        </div>
                      </td>
                      <td className="py-3 px-4"><DistBar distribusi={f.distribusi} jumlah={f.jumlah_respons} /></td>
                    </tr>
                  ))}
                  {data.frontliners.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-sm text-slate-500">
                        Belum ada respons CSAT di periode ini. Kirim tautan survei dari transaksi penjualan dulu.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Komentar terbaru — anonim, tanpa identitas pelanggan */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
              <div className="px-6 py-4 border-b border-slate-100">
                <h3 className="font-display font-semibold text-slate-900">Komentar Terbaru</h3>
                <p className="text-xs text-slate-500">Tanpa identitas pelanggan</p>
              </div>
              <div className="divide-y divide-slate-100 max-h-[560px] overflow-y-auto">
                {data.komentar_terbaru.map((k, i) => (
                  <div key={i} className="px-6 py-4" data-testid={`csat-komentar-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <Stars value={k.rating} size="w-3.5 h-3.5" />
                      <span className="text-[11px] text-slate-400">{fmtTgl(k.submitted_at)}</span>
                    </div>
                    <p className="text-sm text-slate-700 mb-1">"{k.komentar}"</p>
                    <p className="text-xs text-slate-500 inline-flex items-center gap-1">
                      <TrendingUp className="w-3 h-3 text-emerald-500" /> untuk {k.nama_penjual}
                      {k.branch ? ` · ${k.branch}` : ""}
                    </p>
                  </div>
                ))}
                {data.komentar_terbaru.length === 0 && (
                  <p className="px-6 py-10 text-center text-sm text-slate-500">Belum ada komentar di periode ini.</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
